import { createContext, useContext, useEffect, useState } from 'react';

// Theme definitions with color palettes
export const THEMES = {
  reshapers: {
    name: 'Reshapers',
    description: 'Classic purple & magenta gradients',
    colors: {
      primary: '#A2408A',
      secondary: '#674A9D',
      tertiary: '#854097',
      accent: '#CCFF00',
      gradient: 'linear-gradient(135deg, #674A9D 0%, #854097 50%, #A2408A 100%)',
      gradientText: 'linear-gradient(135deg, #674A9D 0%, #854097 50%, #A2408A 100%)',
    },
    preview: ['#674A9D', '#854097', '#A2408A'],
  },
  teambuilding: {
    name: 'Team Building',
    description: 'Energetic orange & gold',
    colors: {
      primary: '#FF9602',
      secondary: '#FFB902',
      tertiary: '#FF7403',
      accent: '#FFB902',
      gradient: 'linear-gradient(135deg, #FFB902 0%, #FF9602 50%, #FF7403 100%)',
      gradientText: 'linear-gradient(135deg, #FFB902 0%, #FF9602 50%, #FF7403 100%)',
    },
    preview: ['#FFB902', '#FF9602', '#FF7403'],
  },
  marcom: {
    name: 'MarCom',
    description: 'Professional blue & teal',
    colors: {
      primary: '#1C54CD',
      secondary: '#4FB4CC',
      tertiary: '#2A70AB',
      accent: '#439A7A',
      gradient: 'linear-gradient(135deg, #4FB4CC 0%, #1C54CD 50%, #2A70AB 100%)',
      gradientText: 'linear-gradient(135deg, #4FB4CC 0%, #1C54CD 50%, #439A7A 100%)',
    },
    preview: ['#4FB4CC', '#1C54CD', '#439A7A'],
  },
};

// UI Mode (light/dark)
export const UI_MODES = {
  dark: {
    name: 'Dark',
    background: '#0D0118',
    surface: 'rgba(255, 255, 255, 0.05)',
    surfaceHover: 'rgba(255, 255, 255, 0.1)',
    border: 'rgba(255, 255, 255, 0.1)',
    text: '#FFFFFF',
    textMuted: '#E2D9F3',
    textSubtle: '#6B5E7C',
  },
  light: {
    name: 'Light',
    background: '#F5F3F7',
    surface: 'rgba(255, 255, 255, 0.9)',
    surfaceHover: 'rgba(255, 255, 255, 1)',
    border: 'rgba(0, 0, 0, 0.1)',
    text: '#1a1a2e',
    textMuted: '#4a4a6a',
    textSubtle: '#8a8aa0',
  },
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children, theme = 'reshapers', initialMode = null }) => {
  const [currentTheme, setCurrentTheme] = useState(theme);
  const [uiMode, setUiMode] = useState(() => {
    // Check localStorage first, then use initialMode, default to 'dark'
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('ui_mode');
      if (saved && UI_MODES[saved]) return saved;
    }
    return initialMode || 'dark';
  });

  useEffect(() => {
    setCurrentTheme(theme);
  }, [theme]);

  useEffect(() => {
    const themeConfig = THEMES[currentTheme] || THEMES.reshapers;
    const modeConfig = UI_MODES[uiMode] || UI_MODES.dark;
    const root = document.documentElement;
    
    // Set theme CSS variables
    root.style.setProperty('--theme-primary', themeConfig.colors.primary);
    root.style.setProperty('--theme-secondary', themeConfig.colors.secondary);
    root.style.setProperty('--theme-tertiary', themeConfig.colors.tertiary);
    root.style.setProperty('--theme-accent', themeConfig.colors.accent);
    root.style.setProperty('--theme-gradient', themeConfig.colors.gradient);
    root.style.setProperty('--theme-gradient-text', themeConfig.colors.gradientText);
    
    // Set UI mode CSS variables
    root.style.setProperty('--ui-background', modeConfig.background);
    root.style.setProperty('--ui-surface', modeConfig.surface);
    root.style.setProperty('--ui-surface-hover', modeConfig.surfaceHover);
    root.style.setProperty('--ui-border', modeConfig.border);
    root.style.setProperty('--ui-text', modeConfig.text);
    root.style.setProperty('--ui-text-muted', modeConfig.textMuted);
    root.style.setProperty('--ui-text-subtle', modeConfig.textSubtle);
    
    // Update data attributes
    root.setAttribute('data-theme', currentTheme);
    root.setAttribute('data-mode', uiMode);
    
    // Save mode to localStorage
    localStorage.setItem('ui_mode', uiMode);
  }, [currentTheme, uiMode]);

  const toggleMode = () => {
    setUiMode(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const value = {
    theme: currentTheme,
    setTheme: setCurrentTheme,
    themeConfig: THEMES[currentTheme] || THEMES.reshapers,
    themes: THEMES,
    uiMode,
    setUiMode,
    toggleMode,
    modeConfig: UI_MODES[uiMode] || UI_MODES.dark,
    isDark: uiMode === 'dark',
    isLight: uiMode === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default theme if not in provider
    return {
      theme: 'reshapers',
      themeConfig: THEMES.reshapers,
      themes: THEMES,
      setTheme: () => {},
      uiMode: 'dark',
      setUiMode: () => {},
      toggleMode: () => {},
      modeConfig: UI_MODES.dark,
      isDark: true,
      isLight: false,
    };
  }
  return context;
};

// Helper component to preview theme colors
export const ThemePreview = ({ themeKey, selected, onClick }) => {
  const theme = THEMES[themeKey];
  if (!theme) return null;
  
  return (
    <button
      type="button"
      onClick={() => onClick(themeKey)}
      className={`p-4 rounded-xl border-2 transition-all text-left w-full ${
        selected 
          ? 'border-white bg-white/10' 
          : 'border-white/10 bg-white/5 hover:border-white/30'
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        {/* Color swatches */}
        <div className="flex -space-x-1">
          {theme.preview.map((color, idx) => (
            <div
              key={idx}
              className="w-6 h-6 rounded-full border-2 border-[#0D0118]"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <span className={`font-semibold ${selected ? 'text-white' : 'text-[#E2D9F3]'}`}>
          {theme.name}
        </span>
      </div>
      <p className="text-xs text-[#6B5E7C]">{theme.description}</p>
    </button>
  );
};

// Dark/Light Mode Toggle Component
export const ModeToggle = ({ className = "" }) => {
  const { uiMode, toggleMode, isDark } = useTheme();
  
  return (
    <button
      onClick={toggleMode}
      className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 ${
        isDark 
          ? 'bg-white/10 hover:bg-white/20 text-white' 
          : 'bg-black/10 hover:bg-black/20 text-gray-800'
      } ${className}`}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="mode-toggle"
    >
      {/* Sun icon */}
      <svg
        className={`w-4 h-4 transition-all duration-300 ${isDark ? 'opacity-50 scale-75' : 'opacity-100 scale-100 text-amber-500'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
          clipRule="evenodd"
        />
      </svg>
      
      {/* Moon icon */}
      <svg
        className={`w-4 h-4 transition-all duration-300 ${isDark ? 'opacity-100 scale-100 text-blue-300' : 'opacity-50 scale-75'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
      </svg>
    </button>
  );
};

export default ThemeProvider;
